import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import logger from '../../../../utils/logger';

const STATUS_LABEL = {
    not_validated: 'Не проверено',
    valid: 'Формат верный',
    invalid: 'Ошибка формата',
    validation_in_process: 'Регистрируется в Ozon…',
    ship_available: 'Готово к сборке',
    ship_not_available: 'Сборка недоступна',
};

const STATUS_COLOR = {
    not_validated: '#6b7280',
    valid: '#2563eb',
    invalid: '#dc2626',
    validation_in_process: '#d97706',
    ship_available: '#16a34a',
    ship_not_available: '#dc2626',
};

function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

function openBase64Document(base64, contentType, fileName) {
    try {
        const byteChars = atob(base64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const win = window.open(url, '_blank');
        if (!win) {
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName || 'document';
            a.click();
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
        logger.error(e);
    }
}

// `initialPostingNumber` — открыть сразу для конкретного отправления (обычно из
// таблицы «Заказы») без ручного ввода номера.
//
// `mode` определяет, какие шаги показывать (см. правку — маркировка ЧЗ и сборка
// разнесены по разным стадиям заказа):
//   - 'assembly' — только сборка (стадия «Ожидают сборки»);
//   - 'marking'  — только маркировка ЧЗ + отгрузка/перевозка (стадия «Ожидают отгрузки»);
//   - 'full'     — старое поведение, все шаги подряд (по умолчанию, для обратной совместимости).
function OzonFbsShipment({ accountId, initialPostingNumber, mode = 'full' }) {
    const showMarking = mode !== 'assembly';
    const showAssembly = mode !== 'marking';
    const showCarriage = mode !== 'assembly';
    const { request, showStatus } = useApi();
    const [postingNumberInput, setPostingNumberInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [shipment, setShipment] = useState(null); // { posting, items: [{ item, exemplars }] }
    const [marksDraft, setMarksDraft] = useState({}); // postingItemId -> string (одна строка на код)
    const [busy, setBusy] = useState('');
    const [carriage, setCarriage] = useState(null);
    const [departureDate, setDepartureDate] = useState(todayIso());
    const pollTimerRef = useRef(null);

    const postingNumber = shipment?.posting?.posting_number;

    const loadShipment = useCallback(async (num) => {
        if (!num) return;
        setLoading(true);
        try {
            const data = await request(`/api/v1/ozon-ops/${accountId}/fbs/postings/${encodeURIComponent(num)}/shipment`);
            setShipment(data);
            const draft = {};
            (data.items || []).forEach(({ item, exemplars }) => {
                draft[item.id] = (exemplars || []).map(e => e.mark).join('\n');
            });
            setMarksDraft(draft);
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Отправление не найдено (сначала синхронизируйте заказы FBS).');
            setShipment(null);
        } finally {
            setLoading(false);
        }
    }, [accountId, request, showStatus]);

    // Открыто из таблицы «Заказы» с уже известным номером — грузим сразу, без формы.
    useEffect(() => {
        if (initialPostingNumber) loadShipment(initialPostingNumber);
        // eslint-disable-next-line
    }, [initialPostingNumber]);

    const handleLoad = (e) => {
        e.preventDefault();
        loadShipment(postingNumberInput.trim());
    };

    const buildProductsPayload = useCallback(() => {
        if (!shipment) return [];
        return shipment.items.map(({ item, product_id: productId }) => ({
            posting_item_id: item.id,
            product_id: productId || 0,
            marks: (marksDraft[item.id] || '')
                .split('\n')
                .map(s => s.trim())
                .filter(Boolean)
                .map(mark => ({ mark })),
        })).filter(p => p.marks.length > 0);
    }, [shipment, marksDraft]);

    const handleValidate = async () => {
        const products = buildProductsPayload();
        if (products.length === 0) {
            showStatus('error', 'Введите коды маркировки хотя бы для одного товара.');
            return;
        }
        if (products.some(p => !p.product_id)) {
            showStatus('error', 'Не удалось определить товар в каталоге Ozon — синхронизируйте раздел «Атрибуты» и откройте отправление заново.');
            return;
        }
        setBusy('validate');
        try {
            await request(`/api/v1/ozon-ops/${accountId}/fbs/postings/${postingNumber}/exemplars/validate`, {
                method: 'POST',
                body: JSON.stringify({ products }),
            });
            showStatus('success', 'Коды проверены.');
            await loadShipment(postingNumber);
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка проверки кодов.');
        } finally {
            setBusy('');
        }
    };

    const pollStatus = useCallback(async () => {
        const deadline = Date.now() + 60000;
        const tick = async () => {
            try {
                const data = await request(`/api/v1/ozon-ops/${accountId}/fbs/postings/${postingNumber}/exemplars/status`);
                await loadShipment(postingNumber);
                if (data?.status === 'ship_available' || data?.status === 'ship_not_available' || Date.now() > deadline) {
                    return;
                }
            } catch (e) {
                logger.error(e);
                return;
            }
            pollTimerRef.current = setTimeout(tick, 3000);
        };
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = setTimeout(tick, 3000);
    }, [accountId, postingNumber, request, loadShipment]);

    const handleSave = async () => {
        setBusy('save');
        try {
            await request(`/api/v1/ozon-ops/${accountId}/fbs/postings/${postingNumber}/exemplars/save`, { method: 'POST' });
            showStatus('success', 'Коды отправлены в Ozon на регистрацию в Честном Знаке.');
            pollStatus();
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка регистрации кодов.');
        } finally {
            setBusy('');
        }
    };

    const handleShip = async () => {
        if (!shipment) return;
        if (shipment.items.some(({ product_id: productId }) => !productId)) {
            showStatus('error', 'Не удалось определить товар в каталоге Ozon — синхронизируйте раздел «Атрибуты» и откройте отправление заново.');
            return;
        }
        setBusy('ship');
        try {
            const packages = [{
                products: shipment.items.map(({ item, product_id: productId }) => ({
                    product_id: productId,
                    quantity: item.quantity,
                })),
            }];
            await request(`/api/v1/ozon-ops/${accountId}/fbs/postings/${postingNumber}/ship`, {
                method: 'POST',
                body: JSON.stringify({ packages }),
            });
            showStatus('success', 'Отправление собрано.');
            await loadShipment(postingNumber);
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка сборки отправления.');
        } finally {
            setBusy('');
        }
    };

    const deliveryMethodId = shipment?.posting?.delivery_method_id;
    const deliveryMethodName = shipment?.posting?.delivery_method_name;

    const handleCreateCarriage = async () => {
        if (!deliveryMethodId) {
            showStatus('error', 'У отправления не определён метод доставки — обновите заказ (кнопка «Синхронизировать» в «Заказах») и откройте снова.');
            return;
        }
        setBusy('carriage-create');
        try {
            const data = await request(`/api/v1/ozon-ops/${accountId}/carriages`, {
                method: 'POST',
                body: JSON.stringify({
                    delivery_method_id: Number(deliveryMethodId),
                    departure_date: departureDate || null,
                }),
            });
            setCarriage(data);
            showStatus('success', 'Отгрузка создана.');
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка создания отгрузки.');
        } finally {
            setBusy('');
        }
    };

    const handleAddToCarriage = async () => {
        if (!carriage || !postingNumber) return;
        setBusy('carriage-add');
        try {
            await request(`/api/v1/ozon-ops/${accountId}/carriages/${carriage.id}/postings`, {
                method: 'POST',
                body: JSON.stringify({ posting_numbers: [postingNumber] }),
            });
            showStatus('success', 'Отправление добавлено в отгрузку.');
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка добавления в отгрузку.');
        } finally {
            setBusy('');
        }
    };

    const handleApproveCarriage = async () => {
        if (!carriage) return;
        setBusy('carriage-approve');
        try {
            await request(`/api/v1/ozon-ops/${accountId}/carriages/${carriage.id}/approve`, {
                method: 'POST',
                body: JSON.stringify({}),
            });
            showStatus('success', 'Отгрузка подтверждена.');
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка подтверждения отгрузки.');
        } finally {
            setBusy('');
        }
    };

    const handleDocument = async (type, fileLabel) => {
        if (!carriage) return;
        setBusy(`doc-${type}`);
        try {
            const data = await request(`/api/v1/ozon-ops/${accountId}/carriages/${carriage.id}/documents?type=${type}`);
            if (data?.file_content) {
                openBase64Document(data.file_content, data.content_type, data.file_name || fileLabel);
            } else {
                showStatus('info', data?.status
                    ? `Документ ещё не готов (статус Ozon: ${data.status}). Попробуйте через пару минут.`
                    : 'Ozon пока не отдал документ. Попробуйте через пару минут.');
            }
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка получения документа.');
        } finally {
            setBusy('');
        }
    };

    const overallExemplarStatus = useMemo(() => {
        if (!shipment) return null;
        const statuses = shipment.items.flatMap(({ exemplars }) => exemplars.map(e => e.validation_status));
        if (statuses.length === 0) return null;
        if (statuses.every(s => s === 'ship_available')) return 'ship_available';
        if (statuses.some(s => s === 'ship_not_available' || s === 'invalid')) return 'ship_not_available';
        if (statuses.some(s => s === 'validation_in_process')) return 'validation_in_process';
        return statuses[0];
    }, [shipment]);

    return (
        <div className="stack-12">
            {!initialPostingNumber && (
                <form className="stack-8" onSubmit={handleLoad}>
                    <label className="bl__label">Номер отправления (FBS)</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input
                            className="bl__input"
                            style={{ flex: 1, maxWidth: 320 }}
                            type="text"
                            placeholder="Например, 89491381-0072-1"
                            value={postingNumberInput}
                            onChange={(e) => setPostingNumberInput(e.target.value)}
                        />
                        <button type="submit" disabled={loading || !postingNumberInput.trim()}>
                            {loading ? 'Загрузка…' : 'Загрузить'}
                        </button>
                    </div>
                    <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                        Обычно этот шаг не нужен — откройте упаковку по кнопке прямо из
                        «Заказы» → «Ожидают отгрузки». Отправление должно быть уже синхронизировано.
                    </p>
                </form>
            )}

            {loading && <p className="muted" style={{ fontSize: 13 }}>Загрузка отправления…</p>}

            {shipment && (
                <>
                    <div className="stack-8">
                        <h4 style={{ margin: 0 }}>
                            {shipment.posting.posting_number}
                            {' '}
                            <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 13 }}>
                                статус: {shipment.posting.status || '—'}
                            </span>
                        </h4>

                        {showMarking && (
                        <>
                        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                            Шаг 1. Отсканируйте или вставьте код маркировки Честный Знак для каждого экземпляра товара.
                        </p>

                        {shipment.items.map(({ item, exemplars }) => (
                            <div key={item.id} style={{ border: '1px solid var(--border, #ddd)', borderRadius: 8, padding: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                    <strong style={{ fontSize: 13 }}>{item.name || item.offer_id || item.sku} × {item.quantity}</strong>
                                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>SKU: {item.sku || '—'}</span>
                                </div>
                                <label className="bl__label" style={{ marginTop: 8 }}>
                                    Коды маркировки ЧЗ (по одному на строку, {item.quantity} шт.)
                                </label>
                                <textarea
                                    className="bl__input"
                                    style={{ width: '100%', minHeight: 60, fontFamily: 'monospace', fontSize: 12, boxSizing: 'border-box' }}
                                    value={marksDraft[item.id] || ''}
                                    onChange={(e) => setMarksDraft(d => ({ ...d, [item.id]: e.target.value }))}
                                    placeholder="Отсканируйте или вставьте коды ЧЗ..."
                                />
                                {exemplars.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                                        {exemplars.map(ex => (
                                            <span key={ex.id} style={{
                                                fontSize: 12, borderRadius: 4, padding: '2px 6px',
                                                background: '#f3f4f6', color: STATUS_COLOR[ex.validation_status] || '#374151',
                                            }}>
                                                {ex.mark.slice(0, 18)}… — {STATUS_LABEL[ex.validation_status] || ex.validation_status}
                                                {ex.validation_error && ` (${JSON.stringify(ex.validation_error).slice(0, 80)})`}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}

                        {overallExemplarStatus && (
                            <p style={{ margin: 0, fontSize: 13, color: STATUS_COLOR[overallExemplarStatus] || '#374151' }}>
                                Статус проверки: {STATUS_LABEL[overallExemplarStatus] || overallExemplarStatus}
                            </p>
                        )}

                        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                            Шаг 2. Проверьте формат кодов, затем отправьте их на регистрацию в Ozon
                            (это официальная процедура «Честного Знака» — Ozon сверяет коды с системой
                            маркировки; занимает от нескольких секунд до минуты).
                        </p>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button type="button" disabled={busy === 'validate'} onClick={handleValidate}>
                                {busy === 'validate' ? 'Проверка…' : 'Проверить формат кодов'}
                            </button>
                            <button type="button" disabled={busy === 'save'} onClick={handleSave}>
                                {busy === 'save' ? 'Отправка…' : 'Зарегистрировать коды в Ozon'}
                            </button>
                        </div>
                        </>
                        )}

                        {showAssembly && (
                        <>
                        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                            {showMarking
                                ? 'Шаг 3. Когда статус станет «Готово к сборке» — соберите отправление.'
                                : 'Соберите отправление (маркировка ЧЗ выполняется отдельно, на стадии «Ожидают отгрузки»).'}
                        </p>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button type="button" disabled={busy === 'ship'} onClick={handleShip}>
                                {busy === 'ship' ? 'Сборка…' : 'Собрать отправление'}
                            </button>
                        </div>
                        </>
                        )}
                    </div>

                    {showCarriage && (
                    <div className="stack-8" style={{ borderTop: '1px solid var(--border, #eee)', paddingTop: 12 }}>
                        <h4 style={{ margin: 0 }}>{showMarking ? 'Шаг 3. Отгрузка (перевозка)' : 'Отгрузка (перевозка)'}</h4>
                        {!carriage ? (
                            <div className="stack-8">
                                <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                                    Метод доставки: <strong>{deliveryMethodName || '—'}</strong>
                                    {deliveryMethodId ? ` (ID ${deliveryMethodId})` : ''} — определён автоматически по отправлению.
                                </p>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <label className="bl__label" style={{ margin: 0 }}>Дата отгрузки</label>
                                    <input className="bl__input" style={{ width: 160 }} type="date"
                                        value={departureDate} onChange={(e) => setDepartureDate(e.target.value)} />
                                    <button type="button" disabled={busy === 'carriage-create' || !deliveryMethodId} onClick={handleCreateCarriage}>
                                        {busy === 'carriage-create' ? 'Создание…' : 'Создать отгрузку'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="stack-8">
                                <p style={{ margin: 0, fontSize: 13 }}>
                                    Отгрузка #{carriage.carriage_id || carriage.id} — статус: {carriage.status}
                                </p>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <button type="button" disabled={busy === 'carriage-add'} onClick={handleAddToCarriage}>
                                        {busy === 'carriage-add' ? 'Добавление…' : 'Добавить отправление в отгрузку'}
                                    </button>
                                    <button type="button" disabled={busy === 'carriage-approve'} onClick={handleApproveCarriage}>
                                        {busy === 'carriage-approve' ? 'Подтверждение…' : 'Подтвердить отгрузку'}
                                    </button>
                                </div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <button type="button" disabled={busy === 'doc-labels'} onClick={() => handleDocument('labels', 'labels.pdf')}>Этикетки</button>
                                    <button type="button" disabled={busy === 'doc-barcode'} onClick={() => handleDocument('barcode', 'barcode.png')}>Штрихкод (PDF/PNG)</button>
                                    <button type="button" disabled={busy === 'doc-barcode_text'} onClick={() => handleDocument('barcode_text', 'barcode.txt')}>Штрихкод (текст)</button>
                                    <button type="button" disabled={busy === 'doc-act'} onClick={() => handleDocument('act', 'act.json')}>Акт / список отправлений</button>
                                </div>
                            </div>
                        )}
                    </div>
                    )}
                </>
            )}
        </div>
    );
}

export default OzonFbsShipment;
